import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  ArrowRight, Check, ChevronRight, FileText, Users, Bell, 
  Building2, BarChart3, TrendingUp, Play, AlertTriangle,
  Shield, Zap, Target, Clock
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import DemoRequestModal from "../components/DemoRequestModal";
import VideoModal from "../components/VideoModal";

const DEMO_VIDEO_URL = "https://customer-assets-eiarnc6j.emergentagent.net/job_praqio-portal/artifacts/qj4wbm6l_Praqio_Demo_v7.mp4";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const stats = [
  { number: "30-50%", label: "Increase in Productivity" },
  { number: "Zero", label: "Missed Expirations" },
  { number: "Faster", label: "Credentialing Turnaround" },
  { number: "100%", label: "Operations Visibility" }
];

const platformFeatures = [
  "Credentialing & payer enrollments",
  "Provider records & compliance",
  "Documents & e-signatures",
  "Task & workflow management",
  "Claims & denial tracking",
  "Financial analytics"
];

const coreFeatures = [
  {
    icon: Users,
    title: "Provider Management",
    subtitle: "Keep all provider information in one place.",
    description: "Centralized provider profiles with license, DEA, malpractice tracking. Get expiration alerts and store documents per provider.",
    image: "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxoZWFsdGhjYXJlJTIwcHJvdmlkZXIlMjBkb2N0b3IlMjBwcm9maWxlfGVufDB8fHx8MTc3NDg4NTYzNXww&ixlib=rb-4.1.0&q=85"
  },
  {
    icon: Bell,
    title: "Credentialing",
    subtitle: "Simplify and accelerate credentialing workflows.",
    description: "CAQH integration, payer enrollment tracking, automated reminders, and complete credential lifecycle management.",
    image: "https://images.pexels.com/photos/7089620/pexels-photo-7089620.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
  },
  {
    icon: FileText,
    title: "Documents & E-Sign",
    subtitle: "Eliminate document chaos.",
    description: "Secure, encrypted storage with AI-powered classification, built-in e-signatures, and version control.",
    image: "https://images.pexels.com/photos/7580856/pexels-photo-7580856.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
  },
  {
    icon: Target,
    title: "Task & Workflow Engine",
    subtitle: "Run your operations with clarity and structure.",
    description: "Kanban, calendar, and timeline views. Task automation and reminders with full team collaboration.",
    image: "https://images.pexels.com/photos/5452221/pexels-photo-5452221.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
  }
];

const useCases = [
  {
    title: "For RCM Companies",
    benefits: [
      "Manage multiple clients in one platform",
      "Track claims, denials, and AR",
      "Improve team productivity",
      "Standardize workflows"
    ]
  },
  {
    title: "For Medical Practices",
    benefits: [
      "Maintain provider records and documents",
      "Track licenses and credential expirations",
      "Stay audit-ready",
      "Reduce administrative workload"
    ]
  }
];

const whyPraqio = [
  "Credentialing-first architecture",
  "Replace multiple tools with one platform",
  "Built for real-world workflows",
  "Scales as your organization grows"
];

export default function Home() {
  const [email, setEmail] = useState("");
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const ctaRef = useRef(null);

  const handleEmailSubmit = () => {
    if (email && email.includes('@')) {
      setDemoModalOpen(true);
    } else {
      toast.info("Please enter your email to book a demo.", { duration: 3000 });
    }
  };

  return (
    <>
      <DemoRequestModal isOpen={demoModalOpen} onClose={() => setDemoModalOpen(false)} />
      <VideoModal 
        isOpen={videoModalOpen} 
        onClose={() => setVideoModalOpen(false)} 
        videoUrl={DEMO_VIDEO_URL}
      />

      {/* Hero Section */}
      <section className="relative pt-28 pb-12 sm:pt-32 sm:pb-16 md:pt-36 md:pb-20 px-4 sm:px-6 overflow-hidden" data-testid="hero-section">
        <div className="absolute top-0 right-0 w-full h-full opacity-20 pointer-events-none">
          <div className="absolute top-20 right-20 w-64 sm:w-96 h-64 sm:h-96 bg-[#C87961] rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-48 sm:w-80 h-48 sm:h-80 bg-[#8A9A86] rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto relative">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.p 
              variants={fadeInUp}
              className="text-xs tracking-[0.15em] uppercase font-bold text-[#8A9A86] mb-4"
            >
              Built for modern healthcare operations teams
            </motion.p>
            
            <motion.h1 
              variants={fadeInUp}
              className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.1] text-[#1A362D] font-medium mb-6"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
              data-testid="hero-title"
            >
              The Operating System for
              <span className="block text-[#C87961]">RCM Teams & Medical Practices</span>
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-base sm:text-lg leading-relaxed text-[#5C6C65] max-w-xl mb-8"
              data-testid="hero-description"
            >
              Manage credentialing, provider data, documents, and revenue workflows — all in one platform.
            </motion.p>
            
            <motion.div 
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-3"
            >
              <Button 
                className="btn-pill btn-accent text-base px-6 py-5"
                data-testid="hero-cta-demo"
                onClick={() => setDemoModalOpen(true)}
              >
                Book a Demo
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button variant="outline" className="btn-pill btn-outline text-base px-6 py-5" data-testid="hero-cta-watch" onClick={() => setVideoModalOpen(true)}>
                <Play className="mr-2 h-4 w-4" />
                Watch Demo
              </Button>
            </motion.div>
          </motion.div>

          {/* Stats */}
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-14 pt-8 border-t border-[#E2E8D8]"
            data-testid="stats-section"
          >
            {stats.map((stat, index) => (
              <div key={index} className="text-center md:text-left">
                <p className="text-2xl sm:text-3xl font-semibold text-[#1A362D]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {stat.number}
                </p>
                <p className="text-sm text-[#5C6C65] mt-1">{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-12 sm:py-14 md:py-16 px-4 sm:px-6 bg-[#1A362D]" data-testid="problem-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-12 h-12 rounded-xl bg-[#C87961] flex items-center justify-center mx-auto mb-5">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <h2 
              className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight text-white font-medium mb-5"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Healthcare Operations Are Fragmented
            </h2>
            <p className="text-base text-[#8A9A86] leading-relaxed mb-6 max-w-2xl mx-auto">
              Most RCM teams and medical practices rely on a mix of spreadsheets, emails, portals, and disconnected tools to manage their operations. Credentialing is delayed. Documents are scattered. Expirations are missed. Workflows are manual. All of this slows down your revenue and creates unnecessary risk.
            </p>
            <p className="text-lg text-[#C87961] font-medium">
              Your operations shouldn't be the bottleneck to your growth.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-12 sm:py-14 md:py-16 px-4 sm:px-6" data-testid="solution-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-10"
          >
            <motion.p 
              variants={fadeInUp}
              className="text-xs tracking-[0.15em] uppercase font-bold text-[#8A9A86] mb-3"
            >
              The Solution
            </motion.p>
            <motion.h2 
              variants={fadeInUp}
              className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight text-[#1A362D] font-medium mb-4"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              One Platform to Run Your<br className="hidden sm:block" />Entire Operation
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-base text-[#5C6C65] max-w-xl mx-auto mb-8"
            >
              Praqio brings together everything you need to manage your healthcare operations:
            </motion.p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-3xl mx-auto mb-8"
          >
            {platformFeatures.map((feature, index) => (
              <div key={index} className="flex items-center gap-2 bg-white rounded-lg px-4 py-3 border border-[#E2E8D8]">
                <Check size={16} className="text-[#8A9A86] flex-shrink-0" />
                <span className="text-sm text-[#1A362D]">{feature}</span>
              </div>
            ))}
          </motion.div>

          <p className="text-center text-[#5C6C65] font-medium">
            All in one secure, scalable system.
          </p>
        </div>
      </section>

      {/* Core Features */}
      <section className="py-12 sm:py-14 md:py-16 px-4 sm:px-6 bg-white" data-testid="core-features">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-10"
          >
            <motion.p 
              variants={fadeInUp}
              className="text-xs tracking-[0.15em] uppercase font-bold text-[#8A9A86] mb-3"
            >
              Features
            </motion.p>
            <motion.h2 
              variants={fadeInUp}
              className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight text-[#1A362D] font-medium"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Everything You Need to Succeed
            </motion.h2>
          </motion.div>

          <div className="space-y-12 sm:space-y-14">
            {coreFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className={`grid md:grid-cols-2 gap-8 md:gap-12 items-center`}
              >
                <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                  <div className="w-12 h-12 rounded-xl bg-[#1A362D] flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 
                    className="text-xl sm:text-2xl font-medium text-[#1A362D] mb-2"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {feature.title}
                  </h3>
                  <p className="text-[#C87961] font-medium text-sm mb-3">
                    {feature.subtitle}
                  </p>
                  <p className="text-[#5C6C65] leading-relaxed mb-4">
                    {feature.description}
                  </p>
                  <Link to="/features">
                    <Button variant="ghost" className="text-[#C87961] hover:text-[#A8624E] p-0 h-auto">
                      Learn more <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
                <div className={index % 2 === 1 ? 'md:order-1' : ''}>
                  <div className="rounded-2xl overflow-hidden shadow-lg">
                    <img 
                      src={feature.image} 
                      alt={feature.title}
                      className="w-full h-56 sm:h-64 object-cover"
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-12 sm:py-14 md:py-16 px-4 sm:px-6" data-testid="use-cases">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-10"
          >
            <motion.p 
              variants={fadeInUp}
              className="text-xs tracking-[0.15em] uppercase font-bold text-[#8A9A86] mb-3"
            >
              Use Cases
            </motion.p>
            <motion.h2 
              variants={fadeInUp}
              className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight text-[#1A362D] font-medium"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Built for Your Workflow
            </motion.h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {useCases.map((useCase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-[#E2E8D8]"
              >
                <h3 
                  className="text-xl font-medium text-[#1A362D] mb-4"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  {useCase.title}
                </h3>
                <ul className="space-y-2">
                  {useCase.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <Check size={16} className="text-[#8A9A86] flex-shrink-0" />
                      <span className="text-sm text-[#5C6C65]">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Praqio */}
      <section className="py-12 sm:py-14 md:py-16 px-4 sm:px-6 bg-[#1A362D]" data-testid="why-praqio">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#8A9A86] mb-3">
                Why Praqio
              </p>
              <h2 
                className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight text-white font-medium mb-5"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Built for Healthcare Operations — Not Generic Software
              </h2>
              <p className="text-base text-[#8A9A86] leading-relaxed mb-6">
                Praqio is designed specifically for how RCM teams and medical practices actually work.
              </p>
              
              <div className="space-y-3">
                {whyPraqio.map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-[#C87961] flex items-center justify-center flex-shrink-0">
                      <Check size={12} className="text-white" />
                    </div>
                    <span className="text-white text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-[#2B4D42] rounded-2xl p-6">
                <div className="bg-white/10 rounded-xl p-5 mb-4">
                  <h4 className="text-white font-medium text-sm mb-3">Real Impact on Your Operations</h4>
                  <p className="text-[#8A9A86] text-sm mb-4">Teams using Praqio experience:</p>
                  <div className="space-y-2">
                    {[
                      "30–50% increase in productivity",
                      "Faster credentialing turnaround",
                      "Zero missed expirations",
                      "Better visibility across operations",
                      "Reduced manual errors"
                    ].map((item, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Zap size={14} className="text-[#C87961]" />
                        <span className="text-white text-xs">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Testimonial */}
                <div className="bg-white/10 rounded-xl p-5">
                  <p className="text-white text-sm italic mb-3">
                    "Praqio completely streamlined our credentialing and operations. What used to take days now takes hours."
                  </p>
                  <p className="text-[#8A9A86] text-xs">— RCM Company Owner</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-12 sm:py-14 md:py-16 px-4 sm:px-6" data-testid="security-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-12 h-12 rounded-xl bg-[#1A362D] flex items-center justify-center mx-auto mb-5">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <h2 
              className="text-2xl sm:text-3xl tracking-tight leading-tight text-[#1A362D] font-medium mb-4"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Built for Healthcare Compliance
            </h2>
            <p className="text-base text-[#5C6C65] leading-relaxed mb-6 max-w-xl mx-auto">
              Security and compliance are built into every layer of Praqio.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {[
                "HIPAA-ready architecture",
                "End-to-end encryption",
                "Role-based access control",
                "Full audit logs"
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-2 bg-white rounded-full px-4 py-2 border border-[#E2E8D8]">
                  <Check size={14} className="text-[#8A9A86]" />
                  <span className="text-sm text-[#1A362D]">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-14 md:py-16 px-4 sm:px-6" data-testid="cta-section" ref={ctaRef}>
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-[#1A362D] to-[#2B4D42] rounded-2xl p-8 sm:p-12 text-center"
          >
            <h2 
              className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight text-white font-medium mb-4"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Ready to Simplify Your Operations?
            </h2>
            <p className="text-base text-[#8A9A86] max-w-xl mx-auto mb-8">
              See how Praqio can transform your workflows and help your team scale.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
              <Input 
                type="email" 
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50 rounded-full px-5 py-5"
                data-testid="cta-email-input"
                onKeyDown={(e) => e.key === 'Enter' && handleEmailSubmit()}
              />
              <Button 
                className="btn-pill btn-accent px-6 py-5 whitespace-nowrap"
                data-testid="cta-submit"
                onClick={handleEmailSubmit}
              >
                Book a Demo
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>

            <p className="text-xs text-[#8A9A86] mt-6">
              A product of <span className="text-white font-medium">Capital Billing Services, Inc.</span>
            </p>
          </motion.div>
        </div>
      </section>
    </>
  );
}
